import {Injectable} from '@angular/core';
import {isNaN, isNumber, round} from 'lodash-es';
import {DateUtilities} from 'webcommon/legacy-common';
import {Gender, ImoPatient, ImoPatient_Age, ImoPatient_Gender, PatientDto, PatientHeaderDetailsDto, Union2} from 'webcommon/shared';

const femaleRegex = /^f(emale)?$/i;
const maleRegex = /^m(ale)?$/i;

// provides standard values for age and gender to be used with imo requests
@Injectable({
  providedIn: 'root',
})
export class ImoPatientFactory {
  constructor(
    private dateUtilities: DateUtilities,
  ) {
  }

  // this function is entirely based off of ImoPatient(.js)
  // and the interface needs to be preserved for as long as it's downgraded
  create(
    name?: string | null,
    ageInYears?: string | number | null,
    gender?: string | null,
  ): ImoPatient {
    let agePart: ImoPatient_Age;
    const ageInYearsNum = isNumber(ageInYears) ? ageInYears : ageInYears ? parseFloat(ageInYears) : NaN;
    if (!isNaN(ageInYearsNum) && ageInYearsNum >= 0) {
      agePart = {
        hasAge: true,
        standardizedAge: round(ageInYearsNum),
      };
    } else {
      agePart = {
        hasAge: false,
        standardizedAge: null,
      };
    }

    let genderPart: ImoPatient_Gender;
    if (gender && femaleRegex.test(gender)) {
      genderPart = {
        hasGender: true,
        standardizedGender: Gender.F,
      };
    } else if (gender && maleRegex.test(gender)) {
      genderPart = {
        hasGender: true,
        standardizedGender: Gender.M,
      };
    } else {
      genderPart = {
        hasGender: false,
        standardizedGender: null,
      };
    }

    return {
      name,
      age: ageInYears,
      gender,
      ...agePart,
      ...genderPart,
    };
  }

  // this function is entirely based off of ImoPatient(.js).mapFromApi
  // and the interface needs to be preserved for as long as it's downgraded
  mapFromApi(patient: Union2<PatientDto, PatientHeaderDetailsDto> | null): ImoPatient | null {
    if (!patient) {
      return null;
    }

    return this.create(...this._mapFromApi(patient));
  }

  _mapFromApi(patient: Union2<PatientDto, PatientHeaderDetailsDto>): Parameters<ImoPatientFactory['create']> {
    const name = (patient.FirstName ? patient.FirstName : '') + (patient.LastName ? ' ' + patient.LastName : '');

    // BirthDate from PatientDto, DateOfBirth from PatientHeaderDetailsDto
    const birthDate = patient.BirthDate || patient.DateOfBirth;
    // only PatientDto has DeathDate
    const deathDate = patient.DeathDate;
    const age = birthDate ?
      this.dateUtilities.getPersonAge(birthDate, deathDate, 'years').age :
      null;

    // BirthGender from PatientDto, BirthGenderName and BirthGenderAnsi from PatientHeaderDetailsDto
    const gender = patient.BirthGender || patient.BirthGenderName || patient.BirthGenderAnsi;

    return [name, age, gender];
  }
}
